import React, { useState, useEffect, useContext } from "react";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import { formatPhone } from "../../utils/formatPhone";

const CreateCustomerForm = ({ onCreated }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    status_id: "",
    phone_number: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postal_code: "",
    organization_type: "",
    existing_customer_id: "",
    is_existing_customer: false,
    // Tax and payment settings
    tax_exempt: false,
    default_service_tax_rate: 0,
    default_mileage_tax_rate: 0,
    payment_terms: 30,
  });

  const [states, setStates] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);

  // Check if invoice enhancements are enabled for this tenant
  const invoiceEnhancementsEnabled = user?.enable_invoices === true;

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      // Fetch states, statuses, and unlinked customers in parallel
      const [statesRes, statusesRes, customersRes] = await Promise.all([
        secureAxios.get(`${apiBase}/api/states`),
        secureAxios.get(`${apiBase}/api/status`),
        secureAxios.get(`${apiBase}/api/customers/unlinked`),
      ]);

      setStates(statesRes.data);
      setStatuses(statusesRes.data);
      console.log("Unlinked customers response:", customersRes.data);
      setExistingCustomers(customersRes.data);
      if (customersRes.data.length === 0) {
        console.log("No unlinked customers available for this tenant");
      }
    } catch (err) {
      console.error("Error loading form data", err);
      setError("Failed to load form data. Please refresh the page.");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue;

    if (type === "checkbox") {
      newValue = checked;
      // If tax_exempt is checked, reset tax rates to 0
      if (name === "tax_exempt" && checked) {
        setFormData((prev) => ({
          ...prev,
          tax_exempt: true,
          default_service_tax_rate: 0,
          default_mileage_tax_rate: 0,
        }));
        if (error) setError(null);
        return;
      }
    } else if (name === "phone_number") {
      newValue = formatPhone(value);
    } else if (
      name === "default_service_tax_rate" ||
      name === "default_mileage_tax_rate"
    ) {
      // Handle tax rate inputs - allow decimals
      const numValue = parseFloat(value) || 0;
      newValue = Math.max(0, Math.min(25, numValue)); // Clamp between 0-25%
    } else if (name === "payment_terms") {
      newValue = parseInt(value) || 30;
    } else {
      newValue = value;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let payload;

      if (formData.is_existing_customer && formData.existing_customer_id) {
        // Link existing customer
        payload = {
          existing_customer_id: formData.existing_customer_id,
        };
      } else {
        // Create new customer
        if (!formData.name || !formData.email || !formData.status_id || !formData.organization_type) {
          setError("Name, email, status, and organization type are required");
          setLoading(false);
          return;
        }

        const cleanedPhone = formData.phone_number.replace(/\D/g, "");

        payload = {
          name: formData.name.trim(),
          email: formData.email.trim(),
          status_id: formData.status_id,
          phone_number: cleanedPhone,
          address_line_1: formData.address_line_1.trim(),
          address_line_2: formData.address_line_2.trim(),
          city: formData.city.trim(),
          state_id: formData.state_id,
          postal_code: formData.postal_code.trim(),
          organization_type: formData.organization_type.trim(),
          // Tax and payment settings
          tax_exempt: formData.tax_exempt,
          default_service_tax_rate: formData.tax_exempt
            ? 0
            : parseFloat(formData.default_service_tax_rate) || 0,
          default_mileage_tax_rate: formData.tax_exempt
            ? 0
            : parseFloat(formData.default_mileage_tax_rate) || 0,
          payment_terms: parseInt(formData.payment_terms) || 30,
        };
      }

      await secureAxios.post(`${apiBase}/api/customers`, payload);

      alert("Customer created successfully!");

      // Reset form
      setFormData({
        name: "",
        email: "",
        status_id: "",
        phone_number: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state_id: "",
        postal_code: "",
        organization_type: "",
        existing_customer_id: "",
        is_existing_customer: false,
        // Tax and payment settings
        tax_exempt: false,
        default_service_tax_rate: 0,
        default_mileage_tax_rate: 0,
        payment_terms: 30,
      });

      if (onCreated) onCreated();
    } catch (err) {
      console.error("Error creating customer:", err);
      setError(err.response?.data?.error || "Failed to create customer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        {/* Already a Customer Checkbox */}
        <div className="col-12">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="is_existing_customer"
              checked={formData.is_existing_customer}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  is_existing_customer: e.target.checked,
                }))
              }
              disabled={loading}
            />
            <label className="form-check-label" htmlFor="is_existing_customer">
              Link Existing Customer?
            </label>
          </div>
        </div>

        {/* Customer Dropdown */}
        {formData.is_existing_customer && (
          <div className="col-md-6">
            <label className="form-label">
              Select Existing Customer <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              value={formData.existing_customer_id}
              onChange={(e) => {
                const selectedId = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  existing_customer_id: selectedId,
                }));

                if (!selectedId) return;

                const selected = existingCustomers.find(
                  (c) => c.customer_id === selectedId
                );

                if (selected) {
                  setFormData((prev) => ({
                    ...prev,
                    name: selected.name || "",
                    email: selected.email || "",
                    phone_number: formatPhone(selected.phone_number || ""),
                    status_id: selected.status_id || "",
                    organization_type: selected.organization_type || "",
                    address_line_1: selected.address?.address_line_1 || "",
                    address_line_2: selected.address?.address_line_2 || "",
                    city: selected.address?.city || "",
                    state_id: selected.address?.state_id || "",
                    postal_code: selected.address?.postal_code || "",
                    // Tax settings from existing customer
                    tax_exempt: selected.tax_exempt || false,
                    default_service_tax_rate:
                      selected.default_service_tax_rate || 0,
                    default_mileage_tax_rate:
                      selected.default_mileage_tax_rate || 0,
                    payment_terms: selected.payment_terms || 30,
                  }));
                }
              }}
              required={formData.is_existing_customer}
              disabled={loading}
            >
              <option value="">Select...</option>
              {existingCustomers.map((c) => (
                <option key={c.customer_id} value={c.customer_id}>
                  {c.name} ({c.email})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Name */}
        <div className="col-md-6">
          <label className="form-label">
            Name <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={formData.is_existing_customer || loading}
            required={!formData.is_existing_customer}
          />
        </div>

        {/* Email */}
        <div className="col-md-6">
          <label className="form-label">
            Email <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={formData.is_existing_customer || loading}
            required={!formData.is_existing_customer}
          />
        </div>

        {/* Status */}
        <div className="col-md-6">
          <label className="form-label">
            Status <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="status_id"
            value={formData.status_id}
            onChange={handleChange}
            disabled={formData.is_existing_customer || loading}
            required={!formData.is_existing_customer}
          >
            <option value="">Select Status</option>
            {statuses.map((s) => (
              <option key={s.status_id} value={s.status_id}>
                {s.status}
              </option>
            ))}
          </select>
        </div>

        {/* Organization Type */}
        <div className="col-md-6">
          <label className="form-label">
            Organization Type <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="organization_type"
            value={formData.organization_type}
            onChange={handleChange}
            disabled={formData.is_existing_customer || loading}
            required={!formData.is_existing_customer}
          />
        </div>

        {/* Phone Number */}
        <div className="col-md-6">
          <label className="form-label">Phone Number</label>
          <input
            className="form-control"
            type="tel"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            disabled={formData.is_existing_customer || loading}
            placeholder="(123) 456-7890"
          />
        </div>

        {/* Address Section */}
        <div className="col-12">
          <h5 className="border-bottom pb-2">Address Information</h5>
        </div>

        {/* Address Line 1 */}
        <div className="col-12">
          <label className="form-label">Address Line 1</label>
          <input
            className="form-control"
            name="address_line_1"
            value={formData.address_line_1}
            onChange={handleChange}
            disabled={formData.is_existing_customer || loading}
          />
        </div>

        {/* Address Line 2 */}
        <div className="col-12">
          <label className="form-label">Address Line 2</label>
          <input
            className="form-control"
            name="address_line_2"
            value={formData.address_line_2}
            onChange={handleChange}
            disabled={formData.is_existing_customer || loading}
          />
        </div>

        {/* City */}
        <div className="col-md-4">
          <label className="form-label">City</label>
          <input
            className="form-control"
            name="city"
            value={formData.city}
            onChange={handleChange}
            disabled={formData.is_existing_customer || loading}
          />
        </div>

        {/* State Dropdown */}
        <div className="col-md-4">
          <label className="form-label">State</label>
          <select
            className="form-select"
            name="state_id"
            value={formData.state_id}
            onChange={handleChange}
            disabled={formData.is_existing_customer || loading}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.state_id} value={state.state_id}>
                {state.state_code} - {state.state_name}
              </option>
            ))}
          </select>
        </div>

        {/* ZIP Code */}
        <div className="col-md-4">
          <label className="form-label">ZIP Code</label>
          <input
            className="form-control"
            name="postal_code"
            value={formData.postal_code}
            onChange={handleChange}
            disabled={formData.is_existing_customer || loading}
            maxLength="10"
          />
        </div>

        {/* ============================================ */}
        {/* TAX & BILLING SETTINGS SECTION */}
        {/* Only show if invoice enhancements enabled */}
        {/* ============================================ */}
        {!formData.is_existing_customer && invoiceEnhancementsEnabled && (
          <>
            <div className="col-12">
              <h5 className="border-bottom pb-2 mt-3">
                <i className="bi bi-receipt me-2"></i>
                Tax &amp; Billing Settings
              </h5>
              <p className="text-muted small">
                These settings are used when generating invoices for this
                customer.
              </p>
            </div>

            {/* Tax Exempt Checkbox */}
            <div className="col-12">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="tax_exempt"
                  name="tax_exempt"
                  checked={formData.tax_exempt}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="tax_exempt">
                  <strong>Tax Exempt</strong>
                </label>
                <div className="form-text">
                  If checked, no tax will be calculated for invoices to this
                  customer
                </div>
              </div>
            </div>

            {/* Tax Rates (shown when not tax exempt) */}
            {!formData.tax_exempt && (
              <>
                <div className="col-md-4">
                  <label className="form-label">
                    Default Service Tax Rate (%)
                  </label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      name="default_service_tax_rate"
                      value={formData.default_service_tax_rate}
                      onChange={handleChange}
                      min="0"
                      max="25"
                      step="0.001"
                      disabled={loading}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <div className="form-text">
                    Tax rate for services (e.g., 6.5 for 6.5%)
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Default Mileage Tax Rate (%)
                  </label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      name="default_mileage_tax_rate"
                      value={formData.default_mileage_tax_rate}
                      onChange={handleChange}
                      min="0"
                      max="25"
                      step="0.001"
                      disabled={loading}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <div className="form-text">
                    Tax rate for mileage (often 0%)
                  </div>
                </div>
              </>
            )}

            {/* Payment Terms */}
            <div className="col-md-4">
              <label className="form-label">Payment Terms</label>
              <select
                className="form-select"
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="15">Net 15</option>
                <option value="30">Net 30</option>
                <option value="45">Net 45</option>
                <option value="60">Net 60</option>
                <option value="90">Net 90</option>
              </select>
              <div className="form-text">
                Invoice due date = Invoice date + payment terms
              </div>
            </div>
          </>
        )}

        {/* Submit */}
        <div className="col-12">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Creating...
              </>
            ) : (
              "Create Customer"
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={() => {
              setFormData({
                name: "",
                email: "",
                status_id: "",
                phone_number: "",
                address_line_1: "",
                address_line_2: "",
                city: "",
                state_id: "",
                postal_code: "",
                organization_type: "",
                existing_customer_id: "",
                is_existing_customer: false,
                tax_exempt: false,
                default_service_tax_rate: 0,
                default_mileage_tax_rate: 0,
                payment_terms: 30,
              });
              setError(null);
            }}
            disabled={loading}
          >
            Clear
          </button>
        </div>
      </form>

      {/* PHI Notice */}
      <div className="alert alert-info mt-3" role="alert">
        <i className="bi bi-shield-lock"></i> <strong>Privacy Notice:</strong>
        Customer information is Protected Health Information (PHI). All data is
        encrypted and access is logged for HIPAA compliance. A corresponding
        inactive tenant will be created for each new customer.
      </div>
    </div>
  );
};

export default CreateCustomerForm;